import type { RecentTransaction, RecentTransactionType } from './DashboardContext';

export const TIMELINE_FETCH_LIMIT = 800;

export const timelineInstant = (tx: RecentTransaction): number => {
  const raw = String(tx.createdAt || tx.date || '').trim();
  if (!raw) return 0;
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortTimelineTransactions = (rows: RecentTransaction[]): RecentTransaction[] =>
  [...rows].sort((a, b) => {
    const delta = timelineInstant(b) - timelineInstant(a);
    if (delta !== 0) return delta;
    return String(b.reference || '').localeCompare(String(a.reference || ''));
  });

export type TimelineDayGroup = {
  key: string;
  labelDate: Date;
  items: RecentTransaction[];
};

const dayKeyFromMs = (ms: number): string => {
  const d = ms ? new Date(ms) : new Date(0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const groupTimelineByDay = (rows: RecentTransaction[]): TimelineDayGroup[] => {
  const map = new Map<string, RecentTransaction[]>();
  for (const row of rows) {
    const key = dayKeyFromMs(timelineInstant(row));
    const list = map.get(key);
    if (list) list.push(row);
    else map.set(key, [row]);
  }
  return [...map.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, items]) => {
      const [year, month, day] = key.split('-').map(Number);
      return {
        key,
        labelDate: new Date(year, month - 1, day),
        items,
      };
    });
};

export const countTimelineTypes = (
  rows: RecentTransaction[],
): Partial<Record<RecentTransactionType, number>> => {
  const counts: Partial<Record<RecentTransactionType, number>> = {};
  for (const row of rows) {
    counts[row.type] = (counts[row.type] || 0) + 1;
  }
  return counts;
};

export const filterTimelineTransactions = (
  rows: RecentTransaction[],
  opts: { types?: RecentTransactionType[]; query?: string },
): RecentTransaction[] => {
  const q = (opts.query || '').trim().toLowerCase();
  const types = opts.types || [];
  return rows.filter((row) => {
    if (types.length > 0 && !types.includes(row.type)) return false;
    if (!q) return true;
    return [row.reference, row.description, row.user, row.depot, row.batch, row.id, row.clientName]
      .some((value) => String(value || '').toLowerCase().includes(q));
  });
};

export const timelineTxKey = (tx: RecentTransaction, idx = 0): string =>
  `${tx.type}:${tx.id}:${tx.batch || ''}:${tx.createdAt || tx.date || ''}:${idx}`;

export type TimelineDensityTick = {
  key: string;
  tx: RecentTransaction;
  position: number;
  visible: boolean;
};

export type TimelineWindow = {
  start: number;
  end: number;
};

export const TIMELINE_MIN_SPAN_MS = 15_000;

export const timelineRange = (rows: RecentTransaction[]): TimelineWindow | null => {
  if (rows.length === 0) return null;
  const instants = rows.map((row) => timelineInstant(row)).filter((ms) => ms > 0);
  if (instants.length === 0) return null;
  const start = Math.min(...instants);
  const end = Math.max(...instants);
  return { start, end: Math.max(end, start + 1) };
};

export const clampTimelineWindow = (
  win: TimelineWindow,
  full: TimelineWindow,
  minSpanMs = TIMELINE_MIN_SPAN_MS,
): TimelineWindow => {
  const fullSpan = Math.max(full.end - full.start, 1);
  const minSpan = Math.min(Math.max(minSpanMs, 1), fullSpan);
  const span = Math.min(Math.max(win.end - win.start, minSpan), fullSpan);
  let start = win.start;
  let end = start + span;
  if (end > full.end) {
    end = full.end;
    start = end - span;
  }
  if (start < full.start) {
    start = full.start;
    end = start + span;
  }
  return { start, end };
};

export const zoomTimelineWindow = (
  win: TimelineWindow,
  full: TimelineWindow,
  factor: number,
  anchor01 = 0.5,
): TimelineWindow => {
  const span = Math.max(win.end - win.start, 1);
  const nextSpan = span * factor;
  const t = Math.min(1, Math.max(0, anchor01));
  const anchor = win.start + span * t;
  const start = anchor - nextSpan * t;
  return clampTimelineWindow({ start, end: start + nextSpan }, full);
};

export const panTimelineWindow = (
  win: TimelineWindow,
  full: TimelineWindow,
  deltaMs: number,
): TimelineWindow =>
  clampTimelineWindow({ start: win.start + deltaMs, end: win.end + deltaMs }, full);

export const isFullTimelineWindow = (
  win: TimelineWindow | null,
  full: TimelineWindow | null,
): boolean => {
  if (!win || !full) return true;
  return win.start <= full.start && win.end >= full.end;
};

export const buildTimelineDensity = (
  rows: RecentTransaction[],
  window?: TimelineWindow | null,
): TimelineDensityTick[] => {
  if (rows.length === 0) return [];
  const full = timelineRange(rows);
  if (!full) return [];
  const view = window ? clampTimelineWindow(window, full) : full;
  const span = Math.max(view.end - view.start, 1);
  return rows.map((tx, idx) => {
    const position = ((timelineInstant(tx) - view.start) / span) * 100;
    return {
      key: timelineTxKey(tx, idx),
      tx,
      position,
      visible: position >= -3 && position <= 103,
    };
  });
};
